import React from 'react';
import { lookupOr } from '../lib/lookupOr';
import { statusState, type LivenessState, type StatusState } from './soundLiveness';

// STATUS cell (Figma 3966-57906): a soft pill mirroring StatusBadge's tokens.
//   live         — green pill + green dot, "Live"
//   not_visible  — red pill + red dot, "Not visible"  (never "deleted")
//   ended        — grey pill, "Ended"
//   unknown      — neutral, "Checking…" — nothing has observed this post yet
//
// `title` carries the "Not publicly visible" language on hover: the compact
// column label stays "Not visible" while the tooltip conveys that the reason is
// unknowable and never blames the creator. Platform APIs cannot tell deleted
// from privated from region-blocked, so nothing here attributes a removal.
const STATE_STYLE: Record<StatusState, { label: string; cls: string; dot?: string; title?: string }> = {
  live: {
    label: 'Live',
    cls: 'bg-[var(--background-success-light-hover,#ecfdf5)] text-[var(--foreground-success-default,#047857)]',
    dot: 'bg-[var(--foreground-success-default,#047857)]',
  },
  not_visible: {
    label: 'Not visible',
    title:
      'Not publicly visible — the post could not be found on a public check (it may be archived, private, or removed). The reason is not knowable.',
    cls: 'bg-red-100 text-red-700',
    dot: 'bg-red-600',
  },
  ended: {
    label: 'Ended',
    cls: 'bg-[var(--stone-200,#e7e5e4)] text-muted-foreground',
  },
  unknown: {
    label: 'Checking…',
    title: 'No visibility check has run for this post yet.',
    cls: 'bg-muted text-muted-foreground',
  },
};

/**
 * Renders the liveness status. `campaignRunning` composes ENDED (a UI
 * derivation) from campaign state; `liveness` is the backend LIVE/NOT_VISIBLE,
 * omitted when nothing has checked the post.
 */
export function LivenessStatusCell({
  campaignRunning,
  liveness,
}: {
  campaignRunning: boolean;
  liveness?: LivenessState;
}) {
  return <LivenessStatusPill state={statusState(campaignRunning, liveness)} />;
}

/** Renders a finished status, e.g. the Creator Content report's `display_status`. */
export function LivenessStatusPill({ state }: { state: StatusState }) {
  const { label, cls, dot, title } = lookupOr(STATE_STYLE, state, STATE_STYLE.unknown);
  return (
    <span
      title={title}
      className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-3 py-[5px] text-[12px] font-medium uppercase ${cls}`}
    >
      {dot && <span className={`size-1.5 shrink-0 rounded-full ${dot}`} />}
      {label}
    </span>
  );
}
